import { forwardRef, useImperativeHandle, useMemo, useRef } from "react";
// components
import DayRow from "../day-row";
// constants
import { DAYS_OF_WEEK } from "constants/daysOfWeek";
// utils
import offsetInterval from "../../utils/offsetInterval";
import moment from "moment-timezone";

type Props = {
  id: string;
  timezone: TimeZone;
  weekStartDateValue: number;
  startDateValue: number;
  values: WeeklyAvailableIntervals;
  onApply: (arg: WeeklyAvailableIntervals) => void;
};

/**
 * renders UI for editing available hours for days
 */
const ForwardedDays = forwardRef<
  Pick<HTMLFormElement, "checkValidity" | "reset">,
  Props
>(function Days(
  { id, timezone, weekStartDateValue, startDateValue, values, onApply },
  ref,
) {
  const weekValues = useMemo(
    () =>
      values.map((el, i) => {
        const date = moment.tz(weekStartDateValue, timezone).add(i, "days");

        while (date.valueOf() < startDateValue) {
          date.add(1, "week");
        }
        return +date;
      }),
    [startDateValue, timezone, values, weekStartDateValue],
  );

  const inputRefs = useRef<
    (Pick<HTMLFormElement, "checkValidity" | "reset"> | null)[]
  >([]);

  useImperativeHandle(ref, () => ({
    checkValidity: () => {
      const validities = inputRefs.current.map((el) => {
        if (!el) {
          return true;
        }

        return el.checkValidity();
      });

      return validities.every((el) => el);
    },
    reset: () =>
      inputRefs.current.forEach((el) => {
        if (!el) {
          return true;
        }
        return el.reset();
      }),
  }));

  return (
    <ul className="px-0" id={id}>
      {values.map((el, i) => {
        return (
          <DayRow
            ref={(el) => {
              inputRefs.current[i] = el;
            }}
            key={i}
            dateValue={weekValues[i]}
            dayTitle={DAYS_OF_WEEK[i]}
            intervals={el}
            timezone={timezone}
            onApply={(v) =>
              onApply([...values.slice(0, i), v, ...values.slice(i + 1)])
            }
            onApplyToAll={() =>
              onApply(
                values.map((el, d) => {
                  // do not reply to self or null
                  if (d === i || el === null) {
                    return el;
                  }
                  const nextIntervals = (values[i] as ScheduleInterval[]).map(
                    (each) => offsetInterval(each, d - i, timezone),
                  );
                  return nextIntervals;
                }),
              )
            }
          />
        );
      })}
    </ul>
  );
});

export default ForwardedDays;
